import AppRoutes from "@/constants/routes/AppRoutes";
import { DataSource } from "@/config/datasources";
import {
  ConsolidationChangeStatus,
  ConsolidationProgress,
  InboundChange,
  OutboundChange,
} from "@/models/legislation/metadata/ConsolidationMetadata";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";
import type { ExtraMetadata } from "@/models/document-link/DocumentLink";

export type ConsolidationView = "draft" | "verified";

export type ConsolidationUrlParams = {
  changeLaw?: string | null;
  asOf?: string | null;
  view?: ConsolidationView | null;
};

export const CONSOLIDATION_NOT_FOUND_MESSAGE =
  "Dette dokument er endnu ikke konsolideret";

export const parseConsolidationUrlParams = (
  searchParams: URLSearchParams
): ConsolidationUrlParams => {
  const rawView = searchParams.get("view");
  const view: ConsolidationView | null =
    rawView === "verified" || rawView === "draft" ? rawView : null;

  return {
    changeLaw: searchParams.get("change_law"),
    asOf: searchParams.get("as_of"),
    view,
  };
};

export const hasExplicitConsolidationUrlParams = (
  urlParams: ConsolidationUrlParams
): boolean =>
  Boolean(urlParams.changeLaw?.trim() || urlParams.asOf?.trim());

export type ConsolidationRenderTarget = {
  lawExternalId: string;
  changeLaw?: string;
  asOf?: string;
  view: ConsolidationView;
};

const APPROVED: ConsolidationChangeStatus = "APPROVED";

export const hasConsolidationMetadata = (
  meta: RetsinformationMetadata | null | undefined
): boolean => {
  const inbound = meta?.inboundChanges?.length ?? 0;
  const outbound = meta?.outboundChanges?.length ?? 0;
  return inbound > 0 || outbound > 0;
};

export const getConsolidationProgress = (
  changes: ReadonlyArray<{ status: ConsolidationChangeStatus }>
): ConsolidationProgress => {
  let approved = 0;
  let needReview = 0;

  for (const change of changes) {
    if (change.status === APPROVED) {
      approved += 1;
    } else {
      needReview += 1;
    }
  }

  return {
    approved,
    needReview,
    total: changes.length,
  };
};

export const getInboundProgress = (
  inbound: ReadonlyArray<InboundChange> | undefined
): ConsolidationProgress => getConsolidationProgress(inbound ?? []);

export const getOutboundProgress = (
  outbound: ReadonlyArray<OutboundChange> | undefined
): ConsolidationProgress => getConsolidationProgress(outbound ?? []);

export const getLatestApprovedInbound = (
  inbound: ReadonlyArray<InboundChange> | undefined
): InboundChange | null => {
  if (!inbound?.length) return null;

  let latest: InboundChange | null = null;

  for (const entry of inbound) {
    if (entry.status !== APPROVED) continue;
    if (!latest || entry.timelineIndex > latest.timelineIndex) {
      latest = entry;
    }
  }

  return latest;
};

export const getLatestInbound = (
  inbound: ReadonlyArray<InboundChange> | undefined
): InboundChange | null => {
  if (!inbound?.length) return null;

  let latest: InboundChange | null = null;

  for (const entry of inbound) {
    if (!latest || entry.timelineIndex > latest.timelineIndex) {
      latest = entry;
    }
  }

  return latest;
};

export const getLastVerifiedConsolidationDate = (
  inbound: ReadonlyArray<InboundChange> | undefined
): string | null => getLatestApprovedInbound(inbound)?.approvedAt ?? null;

export type ConsolidationViewerPathOptions = {
  changeLaw?: string;
  asOf?: string;
  view?: ConsolidationView;
  timelineRoot?: string;
  timelineAt?: number;
};

/**
 * Appends consolidation params to a path that may already carry a query string
 * (e.g. `AppRoutes.legislationHTML` with `htmlIds` / reference-highlight params).
 */
export const appendConsolidationParamsToPath = (
  path: string,
  options: ConsolidationViewerPathOptions = {}
): string => {
  const params = new URLSearchParams();

  if (options.changeLaw?.trim()) {
    params.set("change_law", options.changeLaw.trim());
  }
  if (options.asOf?.trim()) {
    params.set("as_of", options.asOf.trim());
  }
  if (options.view) {
    params.set("view", options.view);
  }
  if (options.timelineRoot?.trim()) {
    params.set("timeline_root", options.timelineRoot.trim());
  }
  if (options.timelineAt != null && options.timelineAt >= 0) {
    params.set("timeline_at", String(options.timelineAt));
  }

  const qs = params.toString();
  if (!qs) return path;

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${qs}`;
};

export const buildConsolidationViewerPath = (
  anchorLawAccession: string,
  options: ConsolidationViewerPathOptions = {}
): string =>
  appendConsolidationParamsToPath(
    AppRoutes.legislationHTML(anchorLawAccession, DataSource.Retsinformation),
    options
  );

export const buildOutboundConsolidationPath = (
  anchorLawAccession: string,
  changelawExternalId: string
): string =>
  buildConsolidationViewerPath(anchorLawAccession, {
    changeLaw: changelawExternalId,
  });

export type ConsolidationLinkMetadata = Pick<
  ExtraMetadata,
  "destination_change_law" | "destination_as_of" | "destination_consolidated"
>;

/**
 * Consolidation identity stored on a document link created from a consolidated
 * render. Static references pin the exact snapshot; dynamic references only
 * record that the view was consolidated, so they follow the destination to the
 * latest consolidation after the pipeline repoints them at a newer LBK.
 */
export const buildConsolidationLinkMetadata = (
  context: { changeLaw?: string; asOf?: string } | null | undefined,
  isStatic: boolean
): ConsolidationLinkMetadata | undefined => {
  if (!context) return undefined;

  const changeLaw = context.changeLaw?.trim();
  const asOf = context.asOf?.trim();

  if (!isStatic || (!changeLaw && !asOf)) {
    return { destination_consolidated: "true" };
  }

  return {
    ...(changeLaw ? { destination_change_law: changeLaw } : {}),
    ...(asOf ? { destination_as_of: asOf } : {}),
  };
};

/** Consolidation params to reapply when navigating to a legislation document. */
export type ConsolidationNavigationOptions = {
  changeLaw?: string;
  asOf?: string;
  /** Open the latest consolidation instead of a pinned snapshot. */
  latest?: boolean;
};

/**
 * Reads the consolidation identity a document link was created from.
 * Returns null for links created outside a consolidated render (the vast
 * majority), which must keep navigating to plain legislation routes.
 */
export const resolveConsolidationLinkNavigation = (
  metadata: ConsolidationLinkMetadata | undefined
): ConsolidationNavigationOptions | null => {
  if (!metadata) return null;

  const changeLaw = metadata.destination_change_law?.trim();
  const asOf = metadata.destination_as_of?.trim();
  if (changeLaw || asOf) {
    return {
      ...(changeLaw ? { changeLaw } : {}),
      ...(asOf ? { asOf } : {}),
    };
  }

  // Tolerates a real boolean in case the value round-trips through JSONB.
  const consolidated: unknown = metadata.destination_consolidated;
  if (consolidated === "true" || consolidated === true) {
    return { latest: true };
  }

  return null;
};

/** Query params that reproduce a consolidated render for a document link. */
export const consolidationNavigationParams = (
  options: ConsolidationNavigationOptions
): ConsolidationViewerPathOptions =>
  options.latest
    ? { view: "draft" }
    : { changeLaw: options.changeLaw, asOf: options.asOf };

/**
 * Deep-link when opening an anchor LBK from overview/timeline (Phase 5).
 * Returns null when no inbound changelaw exists yet. The viewer always renders
 * the draft (full) consolidation, so no `view` is written into the URL.
 */
export const resolveAnchorOpenNavigation = (
  meta: RetsinformationMetadata,
  anchorExternalId: string
): string | null => {
  const latest = getLatestInbound(meta.inboundChanges);
  if (!latest) return null;
  return buildOutboundConsolidationPath(
    anchorExternalId,
    latest.changelawAccession
  );
};

export type ResolveLegislationConsolidationOptions = {
  documentId: string | undefined;
  urlParams: ConsolidationUrlParams;
  meta: RetsinformationMetadata | null | undefined;
  consolidationFeatureEnabled: boolean;
  timelineRoot?: string | null;
};

/**
 * True when the legislation route document id is the anchor for consolidation
 * (not a changelaw page viewing consolidated text via URL params).
 */
export const isAnchorConsolidationViewerRoute = (
  documentId: string | undefined,
  lawExternalId: string | undefined
): boolean => {
  const trimmedDoc = documentId?.trim();
  const trimmedLaw = lawExternalId?.trim();
  if (!trimmedDoc || !trimmedLaw) return false;
  return trimmedDoc === trimmedLaw;
};

/**
 * Resolves the anchor LBK external id for consolidation API calls.
 * On changelaw pages the route documentId is the changelaw accession, not the anchor.
 */
export const resolveAnchorLawExternalId = (
  documentId: string,
  meta: RetsinformationMetadata | null | undefined,
  timelineRoot?: string | null
): string => {
  if (meta?.inboundChanges?.length) return documentId;

  const trimmedRoot = timelineRoot?.trim();
  if (trimmedRoot && trimmedRoot !== documentId) return trimmedRoot;

  const outboundAnchor = meta?.outboundChanges?.find(
    (entry) => entry.anchorLawAccession?.trim()
  )?.anchorLawAccession;
  if (outboundAnchor?.trim()) return outboundAnchor.trim();

  return documentId;
};

const buildRenderTargetFromUrlParams = (
  documentId: string,
  urlParams: ConsolidationUrlParams,
  options?: {
    meta?: RetsinformationMetadata | null;
    timelineRoot?: string | null;
  }
): ConsolidationRenderTarget | null => {
  if (!documentId.trim()) return null;

  const lawExternalId = options
    ? resolveAnchorLawExternalId(documentId, options.meta, options.timelineRoot)
    : documentId;

  // The viewer always renders the draft (full) consolidation — the approval
  // state is surfaced per change in the document, not by filtering content.
  const view: ConsolidationView = "draft";
  const trimmedChangeLaw = urlParams.changeLaw?.trim();
  const trimmedAsOf = urlParams.asOf?.trim();

  if (trimmedChangeLaw) {
    return {
      lawExternalId,
      changeLaw: trimmedChangeLaw,
      view,
      ...(trimmedAsOf ? { asOf: trimmedAsOf } : {}),
    };
  }

  if (trimmedAsOf) {
    return {
      lawExternalId,
      asOf: trimmedAsOf,
      view,
    };
  }

  return null;
};

/**
 * Resolves Consolidation API parameters from explicit URL params only.
 * Enables manual QA before silver-table metadata is populated.
 */
export const resolveExplicitConsolidationUrlTarget = (
  documentId: string,
  urlParams: ConsolidationUrlParams,
  options?: {
    meta?: RetsinformationMetadata | null;
    timelineRoot?: string | null;
  }
): ConsolidationRenderTarget | null => {
  if (!hasExplicitConsolidationUrlParams(urlParams)) return null;
  return buildRenderTargetFromUrlParams(documentId, urlParams, options);
};

/**
 * Resolves Consolidation API parameters from metadata + URL state.
 * Returns null when the viewer should use silver Retsinformation HTML.
 *
 * Metadata-driven defaults (latest inbound changelaw) apply only when the URL
 * explicitly requests a consolidation view (`view=draft|verified`). Plain
 * legislation routes — e.g. timeline LBK circle clicks — stay on silver; overview
 * entry points deep-link with `change_law` via `resolveAnchorOpenNavigation`.
 */
export const resolveConsolidationRenderTarget = (
  meta: RetsinformationMetadata | null | undefined,
  documentId: string,
  urlParams: ConsolidationUrlParams,
  timelineRoot?: string | null
): ConsolidationRenderTarget | null => {
  const explicit = buildRenderTargetFromUrlParams(documentId, urlParams, {
    meta,
    timelineRoot,
  });
  if (explicit) return explicit;

  if (!urlParams.view) return null;

  if (!hasConsolidationMetadata(meta) || !documentId.trim()) return null;

  const inbound = meta?.inboundChanges ?? [];

  if (inbound.length > 0) {
    const latest = getLatestInbound(inbound);
    if (!latest) return null;

    return {
      lawExternalId: documentId,
      changeLaw: latest.changelawAccession,
      view: "draft",
    };
  }

  return null;
};

/**
 * Chooses consolidated vs silver render path for the legislation viewer.
 * Explicit URL params always win so consolidation DB can be tested without metadata.
 */
export const resolveLegislationConsolidationRenderTarget = ({
  documentId,
  urlParams,
  meta,
  consolidationFeatureEnabled,
  timelineRoot,
}: ResolveLegislationConsolidationOptions): ConsolidationRenderTarget | null => {
  if (!documentId?.trim()) return null;

  const explicit = resolveExplicitConsolidationUrlTarget(documentId, urlParams, {
    meta,
    timelineRoot,
  });
  if (explicit) return explicit;

  if (!consolidationFeatureEnabled) return null;

  return resolveConsolidationRenderTarget(meta, documentId, urlParams, timelineRoot);
};
